import { useCallback, useMemo, useState } from 'react';
import Raven from 'raven-js';
import { useUserInfo } from 'marketing-platform-lib/providers/UserInfoProvider';
import { Metrics } from 'framework-asset-preview-lib/internal/metrics';
import { updateCrmObject } from 'framework-asset-preview-lib/internal/shared/api/crm';
import { IGNORE_ERRORS } from 'framework-asset-preview-lib/internal/shared/constants/sentry';
import { useAssetPreview } from '../providers/AssetPreviewProvider';
import useCrmObject from './useCrmObject';
import useProperties from './useProperties';
import { useUpdateFetchCrmObjectCache } from './useFetchCrmObject';
export default function useSaveCrmObject() {
  const {
    applicationId,
    objectType,
    objectId
  } = useAssetPreview();
  const {
    userInfo
  } = useUserInfo();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(false);
  const sourceCrmObject = useCrmObject();
  const crmObjectProperties = useProperties();
  const updateCrmObjectCache = useUpdateFetchCrmObjectCache();
  const {
    isUngatedFor
  } = useUserInfo();
  const isUngatedForEditableUTM = useMemo(() => isUngatedFor('MO:Campaigns:EditableUTMs'), [isUngatedFor]);
  const saveCrmObject = useCallback(changes => {
    setError(false);
    setIsSaving(true);
    return updateCrmObject({
      applicationId,
      changes,
      crmObject: sourceCrmObject,
      crmObjectProperties,
      objectId,
      objectTypeId: objectType,
      sourceId: userInfo ? userInfo.user.email : '',
      isUngatedForEditableUTM
    }).then(response => {
      Metrics.counter('update-crm-object-success', {
        object_type_id: objectType,
        application_id: applicationId
      }).increment();
      setIsSaving(false);
      const updatedCrmObject = updateCrmObjectCache({
        objectId,
        objectType,
        updateFn: existingCache => {
          if (!existingCache) {
            return existingCache;
          }
          /**
           * Cache is only found when preview-lib makes the API call to crm-search.
           */
          const newObject = Object.assign({}, existingCache, {
            properties: Object.assign({}, existingCache.properties, response.properties)
          });
          return newObject;
        }
      });
      if (updatedCrmObject) {
        return updatedCrmObject;
      }

      /**
       * Merge properties from new object with the CRM object that came via property.
       */
      if (sourceCrmObject) {
        return Object.assign({}, sourceCrmObject, {
          properties: Object.assign({}, sourceCrmObject.properties, response.properties)
        });
      }
      return response;
    }).catch(updateError => {
      Metrics.counter('update-crm-object-failure', {
        object_type_id: objectType,
        application_id: applicationId
      }).increment();
      setError(true);
      setIsSaving(false);
      Raven.captureException(new Error('SAVE_CRM_OBJECT_ERROR'), {
        extra: {
          error: updateError.message,
          objectTypeId: objectType,
          properties: Object.keys(changes)
        },
        fingerprint: ['SAVE_CRM_OBJECT_ERROR'],
        tags: {
          lib: 'framework-asset-preview-lib'
        },
        ignoreErrors: IGNORE_ERRORS
      });
      throw updateError;
    });
  }, [applicationId, sourceCrmObject, crmObjectProperties, objectId, objectType, userInfo, isUngatedForEditableUTM, updateCrmObjectCache]);
  return useMemo(() => ({
    error,
    isSaving,
    saveCrmObject
  }), [error, isSaving, saveCrmObject]);
}